import React from "react";
import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuGroup,
  MenuDivider,
  Tooltip,
  Portal,
} from "@chakra-ui/react";
import {
  ChevronDownIcon,
  EditIcon,
  RepeatIcon,
  ExternalLinkIcon,
  AttachmentIcon,
  DownloadIcon,
  ViewIcon,
  CopyIcon,
  ArrowUpDownIcon,
  CheckCircleIcon,
} from "@chakra-ui/icons";

/**
 * OrderActionsMenu
 * Compact “Actions” button with row actions.
 */
const OrderActionsMenu = ({
  order,
  packingLoading = false,
  onViewInvoice,
  onCreateInvoice,
  onSyncInvoice,
  onUpdateTracking,
  onCreatePackingList,
  onViewPackingList,
  onRegeneratePackingList,
  onEditPackingList,        // 👈 NEW: edit packing list
  onCloneOrder,
  onEditOrder,
  onViewPdf,
  onRegenerateOrderPdf,
    onEmailOrderPdf,          // 👈 NEW: email order PDF to distributor
}) => {
  const hasInvoice = Boolean(order?.invoiceId);
  const hasPacking = Boolean(order?.packingId);
  const hasPdf = Boolean(order?.orderPdfUrl);

  return (
    <Menu isLazy>
      <Tooltip label="Open order actions" hasArrow>
        <MenuButton
          as={Button}
          size="sm"
          rightIcon={<ChevronDownIcon />}
          colorScheme="blue"
          _hover={{ transform: "scale(1)" }}
        >
          Actions
        </MenuButton>
      </Tooltip>

      <Portal>
        <MenuList minW="260px" zIndex={1500}>
          <MenuGroup title="Invoice">
            {hasInvoice ? (
              <>
                <MenuItem onClick={() => onViewInvoice?.(order)} icon={<ViewIcon />}>
                  View Invoice
                </MenuItem>
                <MenuItem onClick={() => onSyncInvoice?.(order)} icon={<RepeatIcon />}>
                  Sync Invoice with Order
                </MenuItem>
              </>
            ) : (
              <MenuItem onClick={() => onCreateInvoice?.(order)} icon={<CheckCircleIcon />}>
                Create Invoice
              </MenuItem>
            )}
          </MenuGroup>

          <MenuDivider />

          <MenuGroup title="Shipping & Packing">
            <MenuItem onClick={() => onUpdateTracking?.(order)} icon={<ArrowUpDownIcon />}>
              Update Tracking
            </MenuItem>

            {!hasPacking && hasInvoice && (
              <MenuItem
                onClick={() => onCreatePackingList?.(order)}
                icon={<AttachmentIcon />}
                isDisabled={packingLoading}
              >
                Create Packing List
              </MenuItem>
            )}

            {hasPacking && (
              <>
                <MenuItem onClick={() => onViewPackingList?.(order)} icon={<ExternalLinkIcon />}>
                  View Packing List
                </MenuItem>
                <MenuItem
                  onClick={() => onEditPackingList?.(order)}     // 👈 NEW
                  icon={<EditIcon />}
                  isDisabled={packingLoading}
                >
                  Edit Packing List
                </MenuItem>
                <MenuItem
                  onClick={() => onRegeneratePackingList?.(order)}
                  icon={<RepeatIcon />}
                  isDisabled={packingLoading}
                >
                  Regenerate Packing List
                </MenuItem>
              </>
            )}
          </MenuGroup>

          <MenuDivider />

          <MenuGroup title="Order PDF">
            <MenuItem onClick={() => onRegenerateOrderPdf?.(order)} icon={<DownloadIcon />}>
              Regenerate Order PDF
            </MenuItem>
            <MenuItem onClick={() => onViewPdf?.(order)} icon={<ExternalLinkIcon />} isDisabled={!hasPdf}>
              View PDF
            </MenuItem>
            <MenuItem
              onClick={() => onEmailOrderPdf?.(order)}
              icon={<ExternalLinkIcon />}
              isDisabled={!hasPdf}
            >
              Email Order to Distributor
            </MenuItem>
          </MenuGroup>

          <MenuDivider />

          <MenuGroup title="Admin">
            <MenuItem onClick={() => onCloneOrder?.(order)} icon={<CopyIcon />}>
              Reorder for Subsequent Application
            </MenuItem>
            <MenuItem onClick={() => onEditOrder?.(order)} icon={<EditIcon />}>
              Edit Order
            </MenuItem>
          </MenuGroup>
        </MenuList>
      </Portal>
    </Menu>
  );
};

export default OrderActionsMenu;
